import { Injectable } from '@angular/core';

export interface DataTableConfig {
  pageLength?: number;
  searching?: boolean;
  ordering?: boolean;
  responsive?: boolean;
  scrollX?: boolean;
  autoWidth?: boolean;
  order?: number[][];
  columnDefs?: any[];
  language?: any;
  dom?: string;
  paging?: boolean;
  info?: boolean;
  lengthChange?: boolean;
  processing?: boolean;
  serverSide?: boolean;
  ajax?: any;
  columns?: any[];
  [key: string]: any; // Allow additional DataTables options
}

@Injectable({
  providedIn: 'root'
})
export class DataTablesConfigService {
  
  /**
   * Get default DataTables configuration
   * @param customConfig - Optional custom configuration to override defaults
   * @returns DataTables configuration object
   */
  getDefaultConfig(customConfig: Partial<DataTableConfig> = {}): DataTableConfig {
    const defaultConfig: DataTableConfig = {
      pageLength: 10,
      searching: true,
      ordering: true,
      responsive: true,
      scrollX: true,
      autoWidth: false,
      paging: true,
      info: true,
      lengthChange: true,
      processing: false,
      serverSide: false,
      language: {
        search: "Search:",
        lengthMenu: "Show _MENU_ entries",
        info: "Showing _START_ to _END_ of _TOTAL_ entries",
        infoEmpty: "Showing 0 to 0 of 0 entries",
        infoFiltered: "(filtered from _MAX_ total entries)",
        paginate: {
          first: "First",
          last: "Last",
          next: "Next",
          previous: "Previous"
        },
        emptyTable: "No data available in table",
        zeroRecords: "No matching records found"
      }
    };

    // Merge custom config with defaults
    return { ...defaultConfig, ...customConfig };
  }

  /**
   * Get simple DataTables configuration (minimal options)
   * @param customConfig - Optional custom configuration to override defaults
   * @returns DataTables configuration object
   */
  getSimpleConfig(customConfig: Partial<DataTableConfig> = {}): DataTableConfig {
    return this.getDefaultConfig({
      pageLength: 10,
      searching: true,
      ordering: true,
      ...customConfig
    });
  }

  /**
   * Get configuration with disabled actions column
   * @param actionsColumnIndex - Index of the actions column (default: last column)
   * @param customConfig - Optional custom configuration
   * @returns DataTables configuration object
   */
  getConfigWithActionsColumn(
    actionsColumnIndex: number,
    customConfig: Partial<DataTableConfig> = {}
  ): DataTableConfig {
    return this.getDefaultConfig({
      columnDefs: [
        { targets: [actionsColumnIndex], orderable: false, searchable: false }
      ],
      ...customConfig
    });
  }

  /**
   * Get configuration for server-side processing
   * @param ajaxConfig - Ajax configuration for server-side data
   * @param customConfig - Optional custom configuration
   * @returns DataTables configuration object
   */
  getServerSideConfig(
    ajaxConfig: any,
    customConfig: Partial<DataTableConfig> = {}
  ): DataTableConfig {
    return this.getDefaultConfig({
      serverSide: true,
      processing: true,
      ajax: ajaxConfig,
      ...customConfig
    });
  }
}

